
import { Component, Vue, Watch } from 'vue-property-decorator';
import WebsiteRepositoryInterface, { WebsiteComposite } from '@/services/Website/interfaces';
import { DnsCheckerRepositoryInterface } from '@/services/DnsChecker';
import { CertificatesRepositoryInterface } from '@/services/SSL/interfaces';
import WebsiteLogRepositoryInterface from '@/services/WebsiteLog/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import WebsiteStagingRepositoryInterface from '@/services/WebsiteStaging/interfaces';
import WebsiteMigrationsRepositoryInterface from '@/services/WebsiteMigrations/interfaces';
import WPCliRepositoryInterface from '@/services/WPCli/interfaces';
import DNSRepositoryInterface from '@/services/DNS/Interfaces';
import { DatabaseRepositoryInterface } from '@/services/Database/interfaces';
import { MailboxRepositoryInterface } from '@/services/Mailbox/interfaces';
import { FtpUserRepositoryInterface } from '@/services/FtpUser/interfaces';
import OriginVhostRepositoryInterface from '@/services/Vhost/Origin/Interfaces';
import RedisCacheRepositoryInterface from '@/services/RedisCache/interfaces/redis-cache.interface';
import { SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import CommandInput from '@/components/website/command-console/CommandInput.vue';
import TranscriptEntry from '@/components/website/command-console/TranscriptEntry.vue';
import ConsoleInspector from '@/components/website/command-console/ConsoleInspector.vue';
import { applySuggestion, buildConsoleSuggestions } from '@/components/website/command-console/autocomplete';
import {
  ConsoleActionExecutorDeps,
  executeConsoleActionMutation,
} from '@/components/website/command-console/actionExecutor';
import { isMetaConsoleCommand } from '@/components/website/command-console/commands';
import { executeConsoleCommand, loadMoreSearch } from '@/components/website/command-console/executor';
import {
  clearTranscriptStorage,
  loadCommandHistory,
  loadSiteContext,
  loadTranscript,
  pushCommandHistory,
  saveSiteContext,
  saveTranscript,
} from '@/components/website/command-console/history';
import { normalizeConsoleInput, parseConsoleInput } from '@/components/website/command-console/parser';
import {
  ConsoleActionResult,
  ConsoleLogsResult,
  ConsoleSiteContext,
  ConsoleSuggestion,
  ConsoleTranscriptEntry,
} from '@/components/website/command-console/types';

interface WebsiteLogsMessageInterface {
  websiteId: number;
  domain: string;
  logs: string[];
}

interface ActionSubmitEvent {
  entryId: string;
  payload: any;
}

const LOGS_TIMEOUT_MS = 20000;

@Component({
  components: {
    CommandInput,
    TranscriptEntry,
    ConsoleInspector,
  },
  inject: [
    'websiteRepository',
    'dnsCheckerRepository',
    'certificateRepository',
    'websiteLogRepository',
    'serverRepository',
    'websiteStagingRepository',
    'wpCliRepository',
    'websiteMigrationsRepository',
    'dnsRepository',
    'databaseRepository',
    'mailboxRepository',
    'ftpUserRepository',
    'originVhostRepository',
    'redisCacheRepository',
    'systemUserRepository',
  ],
  sockets: {
    requestLogs(msg: WebsiteLogsMessageInterface): void {
      (this as WebsiteCommandConsole).handleLogsMessage(msg);
    },
    websiteCreation(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('creation');
    },
    websiteCreationAction(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('creation-action');
    },
    websiteDeletion(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('deletion');
    },
    websiteToggle(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('toggle');
    },
    websiteToggleAction(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('toggle-action');
    },
    redisCacheStatusUpdated(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('redis');
    },
    redisCacheStatusUpdateError(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('redis-error');
    },
    getRedisCacheStatus(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('redis');
    },
    wpCheckResult(): void {
      (this as WebsiteCommandConsole).handleAsyncProgress('wp-check');
    },
  },
})
export default class WebsiteCommandConsole extends Vue {
  private websiteRepository!: WebsiteRepositoryInterface;
  private dnsCheckerRepository!: DnsCheckerRepositoryInterface;
  private certificateRepository!: CertificatesRepositoryInterface;
  private websiteLogRepository!: WebsiteLogRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private websiteStagingRepository!: WebsiteStagingRepositoryInterface;
  private wpCliRepository!: WPCliRepositoryInterface;
  private websiteMigrationsRepository!: WebsiteMigrationsRepositoryInterface;
  private dnsRepository!: DNSRepositoryInterface;
  private databaseRepository!: DatabaseRepositoryInterface;
  private mailboxRepository!: MailboxRepositoryInterface;
  private ftpUserRepository!: FtpUserRepositoryInterface;
  private originVhostRepository!: OriginVhostRepositoryInterface;
  private redisCacheRepository!: RedisCacheRepositoryInterface;
  private systemUserRepository!: SystemUserRepositoryInterface;

  public input = '';
  public entries: ConsoleTranscriptEntry[] = [];
  public suggestions: ConsoleSuggestion[] = [];
  public suggestLoading = false;
  public commandHistory: string[] = [];
  public loadingMoreId: string | null = null;
  public siteContext: ConsoleSiteContext | null = null;
  public inFlightCount = 0;
  public inspectorOpen = false;
  public contextComposite: WebsiteComposite | null = null;
  public contextLoading = false;
  public liveAnnouncement = '';

  public starterExamples: string[] = [
    '/help',
    '/actions',
    '/errors',
    '/use example.com',
    '/health',
    '/sites status:ERROR',
  ];

  private suggestRequestId = 0;
  private suggestTimer: number | null = null;
  private entrySeq = 0;
  private pendingLogs: {
    entryId: string;
    websiteId: number;
    timer: number;
  } | null = null;
  private deepLinkHandled = false;
  private contextRequestId = 0;

  public get userScopes(): string[] {
    return (this.$store.getters.scopes as string[]) || [];
  }

  public get socketConnected(): boolean {
    const sock = (this as any).$socket;
    return !!(sock && sock.connected);
  }

  public created(): void {
    this.commandHistory = loadCommandHistory();
    this.siteContext = loadSiteContext();
    this.entries = loadTranscript();
    if (this.entries.length) {
      const lastId = this.entries[this.entries.length - 1].id;
      const match = lastId.match(/entry-(\d+)-(\d+)/);
      if (match) {
        this.entrySeq = Math.max(this.entrySeq, parseInt(match[2], 10) + 1);
      }
    }
  }

  public mounted(): void {
    this.focusInput();
    this.handleDeepLink();
    this.loadContextComposite();
    if (this.entries.length) {
      this.scrollTranscriptToBottom();
    }
  }

  public beforeDestroy(): void {
    this.persistTranscript();
    if (this.suggestTimer !== null) {
      window.clearTimeout(this.suggestTimer);
      this.suggestTimer = null;
    }
    if (this.pendingLogs) {
      window.clearTimeout(this.pendingLogs.timer);
      this.pendingLogs = null;
    }
  }

  @Watch('input')
  public onInputChange(): void {
    this.scheduleSuggestions();
  }

  @Watch('siteContext')
  public onSiteContextChange(): void {
    this.loadContextComposite();
  }

  @Watch('entries')
  public onEntriesChange(): void {
    this.persistTranscript();
  }

  public focusInput(): void {
    const input = this.$refs.commandInput as CommandInput | undefined;
    if (input) {
      input.focus();
    }
  }

  public refreshSuggestions(): void {
    this.scheduleSuggestions(0);
  }

  public scheduleSuggestions(delay = 180): void {
    if (this.suggestTimer !== null) {
      window.clearTimeout(this.suggestTimer);
    }
    this.suggestTimer = window.setTimeout(() => {
      this.loadSuggestions();
    }, delay);
  }

  public async loadSuggestions(): Promise<void> {
    if (this.inFlightCount > 0) {
      return;
    }

    const requestId = this.suggestRequestId + 1;
    this.suggestRequestId = requestId;
    this.suggestLoading = true;

    try {
      const { requestId: returnedId, suggestions } = await buildConsoleSuggestions(
        this.input,
        this.websiteRepository,
        requestId,
        this.serverRepository,
        {
          context: this.siteContext,
          scopes: this.userScopes,
          composite: this.contextComposite,
        },
      );
      if (returnedId !== this.suggestRequestId) {
        return;
      }
      this.suggestions = suggestions;
    } catch (_e) {
      if (requestId === this.suggestRequestId) {
        this.suggestions = [];
      }
    } finally {
      if (requestId === this.suggestRequestId) {
        this.suggestLoading = false;
      }
    }
  }

  public onSelectSuggestion(suggestion: ConsoleSuggestion): void {
    this.input = applySuggestion(this.input, suggestion);
    this.suggestions = [];
    this.$nextTick(() => this.focusInput());
  }

  public onSubmit(): void {
    const raw = this.input.trim();
    if (!raw) {
      return;
    }
    this.runCommand(raw);
  }

  public onInspectorCommand(command: string): void {
    if (this.inspectorOpen && window.innerWidth < 960) {
      this.inspectorOpen = false;
    }
    this.runCommand(command);
  }

  public handleDeepLink(): void {
    if (this.deepLinkHandled) {
      return;
    }
    const command = this.$route.query.command;
    if (typeof command !== 'string' || !command.trim()) {
      return;
    }
    this.deepLinkHandled = true;
    const decoded = decodeURIComponent(command);
    this.$router.replace({ query: {} }).catch(() => undefined);
    this.$nextTick(() => {
      this.runCommand(decoded);
    });
  }

  public handleLogsMessage(msg: WebsiteLogsMessageInterface): void {
    if (!this.pendingLogs || this.pendingLogs.websiteId !== msg.websiteId) {
      return;
    }

    const entryId = this.pendingLogs.entryId;
    window.clearTimeout(this.pendingLogs.timer);
    this.pendingLogs = null;

    const entry = this.entries.find((item) => item.id === entryId);
    if (!entry || !entry.result || entry.result.kind !== 'logs') {
      return;
    }

    const next: ConsoleLogsResult = {
      ...entry.result,
      logs: msg.logs || [],
      pending: false,
      timedOut: false,
    };
    this.patchEntry(entryId, { result: next });
    this.announce(`Logs received for ${msg.domain || entry.result.website.domain}`);
  }

  public handleAsyncProgress(kind: string): void {
    if (!this.siteContext) {
      return;
    }
    // Refresh inspector context on terminal async events; keep input usable.
    this.loadContextComposite();
    this.patchPendingActionProgress(kind);
  }

  private patchPendingActionProgress(kind: string): void {
    const pending = this.entries.filter(
      (entry) => entry.result
        && entry.result.kind === 'action'
        && (entry.result.phase === 'pending' || entry.busy),
    );
    if (!pending.length) {
      return;
    }

    pending.forEach((entry) => {
      if (!entry.result || entry.result.kind !== 'action') {
        return;
      }
      const message = `Progress update (${kind}) received for ${entry.result.composite
        ? entry.result.composite.website.domain
        : 'context'}.`;
      this.patchEntry(entry.id, {
        result: {
          ...entry.result,
          message,
        },
      });
    });
    this.announce(`Async update: ${kind}`);
  }

  public async loadContextComposite(): Promise<void> {
    if (!this.siteContext) {
      this.contextComposite = null;
      this.contextLoading = false;
      return;
    }

    const requestId = this.contextRequestId + 1;
    this.contextRequestId = requestId;
    this.contextLoading = true;
    const id = this.siteContext.id;

    try {
      const composite = await this.websiteRepository.getWebsiteById(id);
      if (requestId !== this.contextRequestId) {
        return;
      }
      this.contextComposite = composite;
    } catch (_e) {
      if (requestId === this.contextRequestId) {
        this.contextComposite = null;
      }
    } finally {
      if (requestId === this.contextRequestId) {
        this.contextLoading = false;
      }
    }
  }

  public async runCommand(rawCommand: string): Promise<void> {
    let normalized = normalizeConsoleInput(rawCommand);
    if (!normalized) {
      return;
    }

    // /rerun — resolve previous non-meta command
    if (normalized === '/rerun' || normalized.toLowerCase() === '/rerun') {
      const previous = this.commandHistory.find((item) => !isMetaConsoleCommand(item));
      if (!previous) {
        this.appendStaticResult(normalized, {
          kind: 'empty',
          message: 'No previous command to re-run.',
        });
        this.input = '';
        this.suggestions = [];
        this.announce('No previous command to re-run.');
        return;
      }
      normalized = previous;
    }

    this.input = '';
    this.suggestions = [];
    this.commandHistory = pushCommandHistory(normalized);

    const entryId = `entry-${Date.now()}-${this.entrySeq}`;
    this.entrySeq += 1;

    const entry: ConsoleTranscriptEntry = {
      id: entryId,
      input: normalized,
      createdAt: new Date().toLocaleTimeString(),
      loading: true,
      result: null,
    };

    this.entries = this.entries.concat([entry]);
    this.scrollTranscriptToBottom();
    this.inFlightCount += 1;
    this.announce(`Running ${normalized}`);

    try {
      const parsed = parseConsoleInput(normalized);
      const outcome = await executeConsoleCommand(parsed, {
        websiteRepository: this.websiteRepository,
        dnsCheckerRepository: this.dnsCheckerRepository,
        certificateRepository: this.certificateRepository,
        websiteLogRepository: this.websiteLogRepository,
        getUserId: () => String(this.$store.getters.id || ''),
        context: this.siteContext,
        actionDeps: this.buildActionDeps(),
      });

      if (outcome.setContext !== undefined) {
        this.siteContext = outcome.setContext;
        saveSiteContext(outcome.setContext);
      }

      if (outcome.refreshContext) {
        await this.loadContextComposite();
      }

      if (outcome.clearTranscript) {
        this.entries = [];
        clearTranscriptStorage();
        if (this.pendingLogs) {
          window.clearTimeout(this.pendingLogs.timer);
          this.pendingLogs = null;
        }
        this.announce('Transcript cleared');
      } else {
        this.patchEntry(entryId, {
          loading: false,
          result: outcome.result,
        });
        this.announceResult(normalized, outcome.result);
      }

      if (outcome.awaitLogsWebsiteId !== undefined) {
        if (this.pendingLogs) {
          window.clearTimeout(this.pendingLogs.timer);
        }
        const websiteId = outcome.awaitLogsWebsiteId;
        const timer = window.setTimeout(() => {
          if (!this.pendingLogs || this.pendingLogs.entryId !== entryId) {
            return;
          }
          this.pendingLogs = null;
          const current = this.entries.find((item) => item.id === entryId);
          if (!current || !current.result || current.result.kind !== 'logs') {
            return;
          }
          this.patchEntry(entryId, {
            result: {
              ...current.result,
              pending: false,
              timedOut: true,
            },
          });
          this.announce('Logs request timed out');
        }, LOGS_TIMEOUT_MS);
        this.pendingLogs = { entryId, websiteId, timer };
      }

      if (outcome.navigateTo) {
        await this.$router.push(outcome.navigateTo).catch(() => undefined);
        return;
      }

      if (outcome.navigateToWebsiteId !== undefined) {
        await this.$router.push({
          name: 'website_view',
          params: { id: String(outcome.navigateToWebsiteId) },
        });
        return;
      }
    } catch (error) {
      const message = error && (error as any).response && (error as any).response.data
        && ((error as any).response.data.message || (error as any).response.data.error)
        ? String((error as any).response.data.message || (error as any).response.data.error)
        : (error instanceof Error ? error.message : 'Command failed.');

      this.patchEntry(entryId, {
        loading: false,
        result: { kind: 'error', message },
      });
      this.announce(`Error: ${message}`);
    } finally {
      this.inFlightCount = Math.max(0, this.inFlightCount - 1);
      this.$nextTick(() => {
        this.focusInput();
        this.scrollTranscriptToBottom();
      });
    }
  }

  public async onLoadMore(entryId: string): Promise<void> {
    const entry = this.entries.find((item) => item.id === entryId);
    if (!entry || !entry.result || entry.result.kind !== 'search') {
      return;
    }

    this.loadingMoreId = entryId;
    try {
      const next = await loadMoreSearch(this.websiteRepository, entry.result);
      if (next.kind === 'search') {
        this.patchEntry(entryId, { result: next });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load more results.';
      this.patchEntry(entryId, {
        result: { kind: 'error', message },
      });
    } finally {
      this.loadingMoreId = null;
    }
  }

  public async onActionSubmit(event: ActionSubmitEvent): Promise<void> {
    const entry = this.entries.find((item) => item.id === event.entryId);
    if (!entry || !entry.result || entry.result.kind !== 'action') {
      return;
    }
    if (entry.busy || entry.result.phase === 'pending') {
      return;
    }

    const actionResult = entry.result as ConsoleActionResult;
    if (!actionResult.composite) {
      this.patchEntry(event.entryId, {
        busy: false,
        result: {
          ...actionResult,
          phase: 'error',
          errorMessage: 'Action requires a website context.',
        },
      });
      this.announce('Action requires context');
      return;
    }

    this.patchEntry(event.entryId, {
      busy: true,
      result: { ...actionResult, phase: 'pending', errorMessage: undefined },
    });

    try {
      const outcome = await executeConsoleActionMutation(
        actionResult.actionId,
        actionResult.composite,
        event.payload,
        this.buildActionDeps(),
      );

      this.patchEntry(event.entryId, {
        busy: false,
        result: outcome.result,
      });

      if (outcome.refreshContext) {
        await this.loadContextComposite();
      }
      if (outcome.setContext !== undefined) {
        this.siteContext = outcome.setContext;
        saveSiteContext(outcome.setContext);
      }
      if (outcome.openUrl) {
        window.open(outcome.openUrl, '_blank');
      }
      if (outcome.navigateTo) {
        await this.$router.push(outcome.navigateTo).catch(() => undefined);
      }

      this.announce(
        outcome.result.phase === 'error'
          ? `Action error: ${outcome.result.errorMessage || 'failed'}`
          : 'Action completed',
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Action failed.';
      this.patchEntry(event.entryId, {
        busy: false,
        result: {
          ...actionResult,
          phase: 'error',
          errorMessage: message,
        },
      });
      this.announce(`Action error: ${message}`);
    } finally {
      this.$nextTick(() => this.scrollTranscriptToBottom());
    }
  }

  private buildActionDeps(): ConsoleActionExecutorDeps {
    return {
      websiteRepository: this.websiteRepository,
      websiteStagingRepository: this.websiteStagingRepository,
      wpCliRepository: this.wpCliRepository,
      websiteMigrationsRepository: this.websiteMigrationsRepository,
      dnsRepository: this.dnsRepository,
      certificateRepository: this.certificateRepository,
      databaseRepository: this.databaseRepository,
      mailboxRepository: this.mailboxRepository,
      ftpUserRepository: this.ftpUserRepository,
      originVhostRepository: this.originVhostRepository,
      redisCacheRepository: this.redisCacheRepository,
      systemUserRepository: this.systemUserRepository,
      serverRepository: this.serverRepository,
      getUserId: () => String(this.$store.getters.id || ''),
      getScopes: () => this.userScopes,
      context: this.siteContext,
      getJwt: () => String(this.$store.getters.jwt || ''),
    };
  }

  public onActionCancel(entryId: string): void {
    const entry = this.entries.find((item) => item.id === entryId);
    if (!entry || !entry.result || entry.result.kind !== 'action') {
      return;
    }
    this.patchEntry(entryId, {
      busy: false,
      result: {
        kind: 'text',
        message: 'Action cancelled.',
      },
    });
    this.announce('Action cancelled');
    this.focusInput();
  }

  private announce(message: string): void {
    this.liveAnnouncement = '';
    this.$nextTick(() => {
      this.liveAnnouncement = message;
    });
  }

  private announceResult(command: string, result: ConsoleTranscriptEntry['result']): void {
    if (!result) {
      return;
    }
    if (result.kind === 'error') {
      this.announce(`Error: ${result.message}`);
      return;
    }
    if (result.kind === 'empty' || result.kind === 'text') {
      this.announce(result.message);
      return;
    }
    if (result.kind === 'search') {
      this.announce(`${result.count} results for ${command}`);
      return;
    }
    if (result.kind === 'action') {
      this.announce(`Action panel: ${result.actionId}`);
      return;
    }
    this.announce(`Completed ${command}`);
  }

  private appendStaticResult(
    input: string,
    result: ConsoleTranscriptEntry['result'],
  ): void {
    const entryId = `entry-${Date.now()}-${this.entrySeq}`;
    this.entrySeq += 1;
    this.entries = this.entries.concat([{
      id: entryId,
      input,
      createdAt: new Date().toLocaleTimeString(),
      loading: false,
      result,
    }]);
    this.scrollTranscriptToBottom();
  }

  private patchEntry(entryId: string, patch: Partial<ConsoleTranscriptEntry>): void {
    this.entries = this.entries.map((entry) => {
      if (entry.id !== entryId) {
        return entry;
      }
      return {
        ...entry,
        ...patch,
      };
    });
  }

  private persistTranscript(): void {
    saveTranscript(this.entries);
  }

  private scrollTranscriptToBottom(): void {
    this.$nextTick(() => {
      const el = this.$refs.transcript as HTMLElement | undefined;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
